import { loadStripe } from '@stripe/stripe-js';
import { createStripePaymentSession, USE_REAL_STRIPE } from './stripeApi';
import Swal from 'sweetalert2';

// Clé publique Stripe (mode test pour le développement)
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_51QXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX');

// Configuration des packs de crédits
export const CREDIT_PACKS = {
  discovery: {
    id: 'discovery',
    name: 'Pack Découverte',
    credits: 1,
    price: 1200, // Prix en centimes (12€ = 1200 centimes)
    currency: 'eur'
  },
  standard: {
    id: 'standard',
    name: 'Pack Standard',
    credits: 3,
    price: 3000, // 30€ = 3000 centimes
    currency: 'eur'
  },
  premium: {
    id: 'premium',
    name: 'Pack Premium',
    credits: 6,
    price: 5500, // 55€ = 5500 centimes
    currency: 'eur'
  }
};

// Fonction pour créer une session de paiement Stripe
export const createPaymentSession = async (packId, userId, userEmail) => {
  try {
    const pack = CREDIT_PACKS[packId];
    if (!pack) {
      throw new Error('Pack de crédits non trouvé');
    }

    // Créer la session de paiement via notre API
    const session = await createStripePaymentSession({
      packId,
      userId,
      userEmail,
      credits: pack.credits,
      amount: pack.price,
      packName: pack.name
    });

    return session;
  } catch (error) {
    console.error('Erreur createPaymentSession:', error);
    throw error;
  }
};

// Fonction pour rediriger vers Stripe Checkout
export const redirectToCheckout = async (sessionUrl) => {
  if (USE_REAL_STRIPE) {
    // En mode réel, rediriger directement vers Stripe Checkout
    window.location.href = sessionUrl;
    return true;
  } else {
    // En mode simulation, afficher la popup
    const pack = Object.values(CREDIT_PACKS).find(p => sessionUrl.includes(p.id));

    // Simulation réaliste d'un paiement Stripe
    return new Promise((resolve) => {
      Swal.fire({
        title: 'Simulation de paiement Stripe',
        html: `
          <div style="text-align: left; margin-bottom: 20px;">
            <h3 style="color: #635bff; margin-bottom: 15px;">💳 Paiement sécurisé</h3>
            <p style="margin-bottom: 10px;"><strong>Montant:</strong> \${(pack?.price / 100).toFixed(2)}€</p>
            <p style="margin-bottom: 10px;"><strong>Article:</strong> \${pack?.credits} crédits - \${pack?.name}</p>
        </div>
        <div style="margin-bottom: 15px;">
          <label style="display: block; margin-bottom: 5px; font-weight: bold;">Numéro de carte</label>
          <input id="cardNumber" type="text" placeholder="4242 4242 4242 4242" style="width: 100%; padding: 8px; border: 1px solid #ccc; border-radius: 4px;" maxlength="19">
        </div>
        <div style="display: flex; gap: 10px; margin-bottom: 15px;">
          <div style="flex: 1;">
            <label style="display: block; margin-bottom: 5px; font-weight: bold;">Date d'expiration</label>
            <input id="expiry" type="text" placeholder="12/28" style="width: 100%; padding: 8px; border: 1px solid #ccc; border-radius: 4px;" maxlength="5">
          </div>
          <div style="flex: 1;">
            <label style="display: block; margin-bottom: 5px; font-weight: bold;">CVC</label>
            <input id="cvc" type="text" placeholder="123" style="width: 100%; padding: 8px; border: 1px solid #ccc; border-radius: 4px;" maxlength="4">
          </div>
        </div>
        <div style="margin-bottom: 15px;">
          <label style="display: block; margin-bottom: 5px; font-weight: bold;">Nom sur la carte</label>
          <input id="cardName" type="text" placeholder="John Doe" style="width: 100%; padding: 8px; border: 1px solid #ccc; border-radius: 4px;">
        </div>
        <div style="font-size: 12px; color: #666; margin-bottom: 15px;">
          🔒 Paiement sécurisé par Stripe. Utilisez les cartes de test pour cette simulation.
        </div>
      `,
        showCancelButton: true,
        confirmButtonText: 'Payer maintenant',
        cancelButtonText: 'Annuler',
        confirmButtonColor: '#635bff',
        customClass: {
          popup: 'stripe-modal'
        },
        preConfirm: () => {
          const cardNumber = document.getElementById('cardNumber').value;
          const expiry = document.getElementById('expiry').value;
          const cvc = document.getElementById('cvc').value;
          const cardName = document.getElementById('cardName').value;

          // Validation basique
          if (!cardNumber || !expiry || !cvc || !cardName) {
            Swal.showValidationMessage('Veuillez remplir tous les champs');
            return false;
          }

          if (cardNumber.replace(/\s/g, '').length < 13) {
            Swal.showValidationMessage('Numéro de carte invalide');
            return false;
          }

          return { cardNumber, expiry, cvc, cardName };
        }
      }).then((result) => {
        if (result.isConfirmed) {
          // Simulation du traitement du paiement
          Swal.fire({
            title: 'Traitement du paiement...',
            html: 'Veuillez patienter pendant que nous traitons votre paiement.',
            allowOutsideClick: false,
            showConfirmButton: false,
            willOpen: () => {
              Swal.showLoading();
            }
          });

          // Simuler le délai de traitement
          setTimeout(() => {
            Swal.fire({
              icon: 'success',
              title: 'Paiement accepté !',
              html: `
              <div style="text-align: center;">
                <div style="font-size: 48px; color: #10b981; margin: 20px 0;">✓</div>
                <p style="margin-bottom: 10px;"><strong>\${pack.credits} crédits</strong> ont été ajoutés à votre compte</p>
                <p style="color: #666; font-size: 14px;">Transaction ID: stripe_sim_\${Date.now()}</p>
              </div>
            `,
              confirmButtonText: 'Continuer',
              confirmButtonColor: '#10b981'
            });
            resolve(true);
          }, 2000);
        } else {
          resolve(false);
        }
      });
    });
  }
};

// Fonction pour traiter un achat de crédits
export const purchaseCredits = async (packId, userId, userEmail) => {
  try {
    // Créer la session de paiement
    const session = await createPaymentSession(packId, userId, userEmail);

    // En mode simulation, afficher le formulaire de paiement
    if (!USE_REAL_STRIPE) {
      const paymentSuccess = await redirectToCheckout(session.url);
      if (!paymentSuccess) {
        throw new Error('Paiement annulé par l\'utilisateur');
      }
    } else {
      // En mode réel, rediriger vers Stripe (ne retourne pas)
      redirectToCheckout(session.url);
      // Note: la fonction ne retournera jamais car window.location change
    }

    return session;
  } catch (error) {
    console.error('Erreur purchaseCredits:', error);
    throw error;
  }
};

export { stripePromise };
